import { useState, useCallback, useEffect, useRef } from "react";
import { type GlyphResult } from "@/mocks/dreamspellData";
import Calculator from "./components/Calculator";
import ResultDisplay from "./components/ResultDisplay";
import SolarSeals from "./components/SolarSeals";
import Method from "./components/Method";
import Footer from "./components/Footer";

export default function Home() {
  const [result, setResult] = useState<GlyphResult | null>(null);
  const [dateStr, setDateStr] = useState<string>("");
  const [isLeap, setIsLeap] = useState<boolean>(false);
  const resultRef = useRef<HTMLDivElement>(null);

  const handleCalculate = useCallback(
    (res: GlyphResult | null, date: string, leap: boolean) => {
      setResult(res);
      setDateStr(date);
      setIsLeap(leap);
    },
    [],
  );

  useEffect(() => {
    if (dateStr && resultRef.current) {
      resultRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [result, dateStr, isLeap]);

  return (
    <div className="min-h-screen bg-[#f5f0e6]">
      {/* Header / Hero with background image */}
      <header className="relative w-full overflow-hidden min-h-[360px] md:min-h-[440px] flex items-center">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=An%20abstract%20artistic%20illustration%20inspired%20by%20ancient%20Maya%20civilization%2C%20warm%20earth%20tones%20with%20golden%20amber%20and%20beige%20colors%2C%20subtle%20geometric%20sun%20calendar%20patterns%20and%20sacred%20spiral%20motifs%2C%20minimal%20and%20elegant%20design%2C%20no%20text%2C%20soft%20warm%20gradient%20background%2C%20clean%20modern%20aesthetic%20suitable%20for%20a%20website%20hero%20banner&width=1600&height=600&seq=maya-hero-bg&orientation=landscape"
            alt="Maya style background"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50" />
        </div>

        <div className="relative z-10 w-full px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center gap-2 mb-5">
              <div className="w-5 h-5 rounded-full border border-white/70 flex items-center justify-center">
                <div className="w-1.5 h-1.5 rounded-full bg-white/70" />
              </div>
              <span className="text-xs font-medium text-white/80 uppercase tracking-widest">
                DREAMSPELL ORACLE
              </span>
            </div>

            <h1 className="text-3xl md:text-5xl font-bold text-white leading-tight">
              馬雅圖騰計算器
            </h1>
            <p className="mt-2 text-xl md:text-2xl text-white/80 italic font-light">
              Dreamspell 五大印記
            </p>
            <p className="mt-4 text-sm md:text-base text-white/70 leading-relaxed max-w-2xl">
              輸入西元出生年月日，依 Dreamspell 13 月亮曆推算你的 Kin 編號、13
              音頻、20 太陽印記，以及主、支持、挑戰、指引、隱藏五大印記。
            </p>
          </div>
        </div>
      </header>

      <main className="w-full">
        <Calculator onCalculate={handleCalculate} />
        <div ref={resultRef} className="scroll-mt-4">
          <ResultDisplay result={result} dateStr={dateStr} isLeap={isLeap} />
        </div>
        <SolarSeals />
        <Method />
      </main>

      <Footer />
    </div>
  );
}